/**
 * Confirm Dialog Component
 * Part of TASK-334: Frontend Admin Panel
 *
 * A reusable confirmation dialog built on Radix AlertDialog primitives.
 * Supports destructive actions, warning messages, loading states, and disabled confirm.
 */

import React from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './button';

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Dialog title */
  title: string;
  /** Dialog description/message */
  description: string;
  /** Optional warning message displayed in an amber box */
  warning?: string;
  /** Callback when confirm is clicked */
  onConfirm: () => void;
  /** Text for confirm button (default: "Confirm") */
  confirmText?: string;
  /** Text for cancel button (default: "Cancel") */
  cancelText?: string;
  /** Use destructive (red) styling for confirm button */
  destructive?: boolean;
  /** Disable the confirm button */
  confirmDisabled?: boolean;
  /** Show loading spinner on confirm button */
  isLoading?: boolean;
}

/**
 * ConfirmDialog - A standardized confirmation dialog
 *
 * @example
 * ```tsx
 * <ConfirmDialog
 *   open={showDelete}
 *   onOpenChange={setShowDelete}
 *   title="Delete User"
 *   description="Are you sure you want to delete this user? This action cannot be undone."
 *   warning={user.hasClients ? "This user has active clients assigned." : undefined}
 *   onConfirm={handleDelete}
 *   confirmText="Delete"
 *   destructive
 *   isLoading={isDeleting}
 * />
 * ```
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  warning,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = false,
  confirmDisabled = false,
  isLoading = false,
}) => {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal>
        {/* Overlay */}
        <AlertDialogPrimitive.Overlay
          className="
            fixed inset-0 z-50
            bg-black/50 backdrop-blur-sm
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
          "
        />

        {/* Content */}
        <AlertDialogPrimitive.Content
          className="
            fixed left-[50%] top-[50%] z-50
            translate-x-[-50%] translate-y-[-50%]
            w-full max-w-md
            bg-white rounded-xl shadow-xl p-6
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]
            data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]
          "
        >
          {/* Title */}
          <AlertDialogPrimitive.Title className="text-lg font-semibold text-gray-900 mb-2">
            {title}
          </AlertDialogPrimitive.Title>

          {/* Description */}
          <AlertDialogPrimitive.Description className="text-gray-600 mb-4">
            {description}
          </AlertDialogPrimitive.Description>

          {/* Warning box (optional) */}
          {warning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">{warning}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <AlertDialogPrimitive.Cancel asChild>
              <Button variant="outline" disabled={isLoading}>
                {cancelText}
              </Button>
            </AlertDialogPrimitive.Cancel>

            <AlertDialogPrimitive.Action asChild>
              <Button
                variant={destructive ? 'destructive' : 'default'}
                onClick={(e) => {
                  e.preventDefault();
                  onConfirm();
                }}
                disabled={confirmDisabled || isLoading}
              >
                {isLoading && <Loader2 size={16} className="mr-2 animate-spin" />}
                {confirmText}
              </Button>
            </AlertDialogPrimitive.Action>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};

export default ConfirmDialog;
